import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { AccountCenter } from "./account/AccountCenter";
import { DeveloperCenter } from "./developer/DeveloperCenter";
import { AuthScreen, EmailVerificationScreen, RecoveryCompletionScreen, SecondFactorGate, useCurrentSession } from "./auth";
import { ErrorState, LoadingState } from "./account/components";
import { AppealScreen } from "./operator/AppealScreen";
import { OperatorCenter } from "./operator/OperatorCenter";
import { Account, CurrentSession, ImpersonationState, api } from "./api";
import { SetupWizard } from "./setup/SetupWizard";
import { SetupStatus } from "./setup/configuration";
import { ThemeProvider } from "./theme";

export function App() {
  return <ThemeProvider><InstallationBoundary /></ThemeProvider>;
}

function InstallationBoundary() {
  const [setupStarted, setSetupStarted] = useState(false);
  const status = useQuery({
    queryKey: ["setup-status"],
    queryFn: () => api<SetupStatus>("/api/v1/setup/status"),
    staleTime: Number.POSITIVE_INFINITY,
    retry: true,
    refetchInterval: (query) => query.state.data?.mode === "SETUP" ? 1000 : false,
  });
  useEffect(() => {
    if (status.data?.mode === "SETUP") setSetupStarted(true);
  }, [status.data?.mode]);
  if (!status.data && status.isPending) return <main className="bootstrap-state"><LoadingState label="正在确认安装状态" /></main>;
  if (!status.data && status.error) return <main className="bootstrap-state"><ErrorState error={status.error} retry={() => void status.refetch()} /></main>;
  if (setupStarted || status.data?.mode === "SETUP") {
    return <SetupWizard
      phase={status.data?.phase ?? "CONFIGURATION"}
      restartError={status.data?.restartError ?? null}
      onComplete={() => {
        setSetupStarted(false);
        window.location.assign("/login");
      }} />;
  }
  return <RoutedApp />;
}

function RoutedApp() {
  if (window.location.pathname === "/appeal") return <AppealScreen />;
  if (window.location.pathname === "/recover") return <RecoveryCompletionScreen />;
  if (window.location.pathname === "/verify-email") return <EmailVerificationScreen />;
  return <SessionApp />;
}

function SessionApp() {
  const session = useCurrentSession();
  if (session.isPending) return <main className="bootstrap-state"><LoadingState label="正在确认登录状态" /></main>;
  if (session.error) return <main className="bootstrap-state"><ErrorState error={session.error} retry={() => void session.refetch()} /></main>;
  if (!session.data) return <AuthScreen onAuthenticated={() => void session.refetch()} />;
  return <AuthenticatedApp session={session.data} refreshSession={() => void session.refetch()} />;
}

function AuthenticatedApp({ session, refreshSession }: { session: CurrentSession; refreshSession: () => void }) {
  const impersonation = useQuery({
    queryKey: ["impersonation"],
    queryFn: () => api<ImpersonationState>("/api/v1/impersonation"),
    retry: false,
  });
  const account = useQuery({
    queryKey: ["account"],
    queryFn: () => api<Account>("/api/v1/account"),
    retry: false,
  });
  if (account.isPending) return <main className="bootstrap-state"><LoadingState label="正在确认账号状态" /></main>;
  if (account.error || !account.data) return <main className="bootstrap-state"><ErrorState error={account.error} retry={() => void account.refetch()} /></main>;
  let content;
  if (account.data.status === "DELETION_PENDING") {
    content = <AccountCenter session={session} onLoggedOut={refreshSession} />;
  } else if (session.secondFactorRequired) {
    content = <SecondFactorGate session={session} onVerified={refreshSession} />;
  } else if (window.location.pathname.startsWith("/operator")) {
    content = <OperatorCenter account={account.data} session={session} onLoggedOut={refreshSession} />;
  } else if (window.location.pathname.startsWith("/developer")) {
    content = <DeveloperCenter account={account.data} session={session} onLoggedOut={refreshSession} />;
  } else {
    content = <AccountCenter session={session} onLoggedOut={refreshSession} />;
  }
  return <>{impersonation.data?.active && <ImpersonationBanner state={impersonation.data} />}{content}</>;
}

function ImpersonationBanner({ state }: { state: ImpersonationState }) {
  const stop = useQueryClientMutation();
  return <div className="impersonation-banner" role="status"><span>正在以 @{state.subjectUsername} 身份操作，{state.expiresAt ? new Date(state.expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "稍后"} 自动结束</span><button type="button" disabled={stop.isPending} onClick={() => stop.mutate()}>返回管理员账号</button></div>;
}

function useQueryClientMutation() {
  return useMutation({
    mutationFn: () => api<void>("/api/v1/impersonation", { method: "DELETE" }),
    onSuccess: () => window.location.assign("/operator/super-admin"),
  });
}
